import React, { useEffect, useId, useLayoutEffect, useMemo, useState } from "react";
import ApiData from "../../../api/ApiData";
import { SideDrawer, SideDrawerCloseButton } from "@dmc/ui";
import { BtnOutlineV4 } from "../../UI/button/BtnOutlineV4";
import { formatMoneyString } from "../../../utils/integration1cPriceDisplay";
import {
  displayTaxField,
  formatQuantityWithMeasure,
  INVOICES_PAGE_SIZE,
} from "../../../utils/companyInvoicesUtils";

const GRID_CLASS = "cc-v4-soft-grid company-invoice-lines-grid";
const HEADER_CLASS = `lic_container_header cc-v4-soft-header ${GRID_CLASS}`;
const ROW_CLASS = `cc-v4-soft-row ${GRID_CLASS} company-invoice-lines-row`;

function LinesTableHeader() {
  return (
    <div className={HEADER_CLASS}>
      <span className="cc-v4-soft-th-static">
        <span className="cc-v4-soft-th-label">Продукт</span>
      </span>
      <span className="cc-v4-soft-th-static">
        <span className="cc-v4-soft-th-label">Кол-во</span>
      </span>
      <span className="cc-v4-soft-th-static reports-th-numeric">
        <span className="cc-v4-soft-th-label">Цена/шт</span>
      </span>
      <span className="cc-v4-soft-th-static reports-th-numeric">
        <span className="cc-v4-soft-th-label">Итого</span>
      </span>
      <span className="cc-v4-soft-th-static company-invoice-lines-col-tax">
        <span className="cc-v4-soft-th-label">НДС%</span>
      </span>
      <span className="cc-v4-soft-th-static company-invoice-lines-col-tax">
        <span className="cc-v4-soft-th-label">НДС/сумма</span>
      </span>
    </div>
  );
}

function LinesSkeleton() {
  const skeletonRow = `${ROW_CLASS} company-invoices-skeleton-row`;
  return (
    <div className="cc-v4-soft-body" aria-busy="true" aria-label="Загрузка позиций">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={skeletonRow}>
          <span
            className={`company-invoices-skeleton-cell${
              i % 2 === 0 ? "" : " company-invoices-skeleton-cell--short"
            }`}
          />
          <span className="company-invoices-skeleton-cell company-invoices-skeleton-cell--short" />
          <span
            className="company-invoices-skeleton-cell company-invoices-skeleton-cell--numeric"
          />
          <span
            className="company-invoices-skeleton-cell company-invoices-skeleton-cell--numeric"
          />
          <span className="company-invoices-skeleton-cell company-invoice-lines-col-tax" />
          <span className="company-invoices-skeleton-cell company-invoice-lines-col-tax" />
        </div>
      ))}
    </div>
  );
}

function InvoiceLinesPager({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) {
    return null;
  }
  return (
    <div className="company-invoices-pager">
      <span className="company-invoices-pager-label">
        Страница {page} из {totalPages}
      </span>
      <BtnOutlineV4
        text="Назад"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      />
      <BtnOutlineV4
        text="Вперёд"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      />
    </div>
  );
}

export function CompanyInvoiceLinesDrawer({
  open,
  onClose,
  bitrixId,
  invoiceTitle,
}) {
  const titleId = useId();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [positions, setPositions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / INVOICES_PAGE_SIZE));

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setPage(1);
  }, [open, bitrixId]);

  useLayoutEffect(() => {
    if (!open) {
      setLoading(false);
      setPositions([]);
      setTotal(0);
      setError(false);
      return;
    }
    if (bitrixId) {
      setLoading(true);
      setError(false);
    }
  }, [open, bitrixId, page]);

  useEffect(() => {
    if (!open || !bitrixId) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setPositions([]);

    ApiData.getCompanyInvoiceLines(bitrixId, {
      number: page,
      size: INVOICES_PAGE_SIZE,
    }).then((data) => {
      if (cancelled) {
        return;
      }
      if (!data) {
        setError(true);
        setPositions([]);
        setTotal(0);
      } else {
        setPositions(data.positions ?? []);
        setTotal(data.total ?? 0);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, bitrixId, page]);

  const title = useMemo(() => {
    const base = invoiceTitle || "Позиции счёта";
    return base.length > 80 ? `${base.slice(0, 77)}…` : base;
  }, [invoiceTitle]);

  if (!open) {
    return null;
  }

  const showPager = !loading && !error;

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      usePortal={false}
      toolbar={<SideDrawerCloseButton onClick={onClose} title="Закрыть" />}
    >
      <div className="company-invoice-lines-drawer-body">
        <div className="cc-v4-soft-list-root company-invoice-lines-list">
          <LinesTableHeader />

          {loading ? <LinesSkeleton /> : null}

          {!loading && error ? (
            <div className="cc-v4-soft-body">
              <p className="grey_info">Не удалось загрузить позиции счёта</p>
            </div>
          ) : null}

          {!loading && !error && positions.length === 0 ? (
            <div className="cc-v4-soft-body">
              <p className="grey_info">Позиции не найдены</p>
            </div>
          ) : null}

          {!loading && !error && positions.length > 0 ? (
            <div className="cc-v4-soft-body">
              {positions.map((row) => (
                <div key={row.position_id} className={ROW_CLASS}>
                  <span className="reports-cell">{row.product_name}</span>
                  <span className="reports-cell">
                    {formatQuantityWithMeasure(row.quantity, row.measure_name)}
                  </span>
                  <span className="reports-cell reports-cell-numeric">
                    {formatMoneyString(row.unit_price)}
                  </span>
                  <span className="reports-cell reports-cell-numeric">
                    {formatMoneyString(row.position_amount)}
                  </span>
                  <span className="reports-cell company-invoice-lines-col-tax">
                    {displayTaxField(row.tax_name)}
                  </span>
                  <span className="reports-cell company-invoice-lines-col-tax">
                    {displayTaxField(row.tax_rate)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {showPager ? (
          <InvoiceLinesPager
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>
    </SideDrawer>
  );
}
